import { SingingPortraitFragment } from 'api/contentful/contentful.graphql.types'
import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import {
  ContentfulMediaMentions,
  getContentfulMediaMentions,
} from 'api/contentful/getContentfulMediaMention'
import { getStoryPortraitAnimations } from 'api/contentful/memory'
import { apiFetcher } from 'api/goodtrust/api'
import { postAuthTokenCheck, postAuthTokenProcess, postLogin } from 'api/goodtrust/auth'
import { handleAuthResponse } from 'api/goodtrust/auth.util'
import { useSharedUserMemory } from 'api/goodtrust/user/memory'
import { Block } from 'components/Block'
import { HelpCarousel } from 'components/content/helpCarousel/HelpCarousel'
import { HomepageCallToAction } from 'components/content/homepage/homepageCallToAction/HomepageCallToAction'
import { LifeStoriesContextProvider } from 'components/content/lifeStories/LifeStoriesContext'
import { MediaMentionsRow } from 'components/content/mediaMentions/MediaMentionsRow'
import { singingPortraitsHomepageImages } from 'components/content/singingPortraits/content'
import { SharedSPActions } from 'components/content/singingPortraits/headerActions/SharedSPActions'
import { SingingPortraitsHero } from 'components/content/singingPortraits/singingPortraitsHero/SingingPortraitsHero'
import { SingingPortraitsWizardCards } from 'components/content/singingPortraits/singingPortraitsWizardCards/SingingPortraitsWizardCards'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Layout } from 'components/layout/Layout'
import { SelectedMemory } from 'components/memory/MemoryUtils'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { MemoryShareItems } from 'components/share/MemoryShareItems'
import { Spacer } from 'components/spacer/Spacer'
import { BlockSpinner } from 'components/spinner/Spinner'
import { StoryPortraitsLandingPageSection } from 'components/storyPortraits/landingPageSection/StoryPortraitsLandingPageSection'
import { toastError, toastSuccess } from 'components/Toast'
import { nanoid } from 'nanoid'
import { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import Router, { useRouter } from 'next/router'
import { useEffect } from 'react'
import { events, fireEvent, mutateAnalyticsData } from 'utils/analytics'
import { useAuth } from 'utils/auth/hooks/useAuth'
import { hashPassword } from 'utils/auth/password'
import { encodeQuery } from 'utils/fetcher'
import { formatName } from 'utils/format'
import { logError } from 'utils/general'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { SESSION_REFERRAL_MEMORY_UUID, setSessionValue } from 'utils/sessionStorage'
import { ApiType } from 'utils/types'
import { useWindowSize } from 'utils/hooks'
import { useModal } from 'components/modal/Modal'
import { EmailVerificationModal } from 'components/modal/emailVerificationModal/EmailVerificationModal'
import { emailModalConfig } from 'utils/email'

interface PageProps {
  memoryData?: ApiType['MemoryResponse'] | null
  mediaMentions: ContentfulMediaMentions
  faq: ContentfulFAQ
  storyPortraitAnimations: SingingPortraitFragment[]
}

function Page(props: PageProps) {
  const { t } = useTranslation('memory')
  const router = useRouter()
  const memoryUuid = router.query.uuid as string
  const animationUuid = router.query.animationUuid as string
  const memory = useSharedUserMemory(memoryUuid, animationUuid)
  const animation = memory.data?.json?.animated_files?.[0]
  const selectedMemory: SelectedMemory = {
    ...memory.data?.json,
    selectedAnimation: animation || null,
  }
  const { isLogged } = useAuth()
  const { isMobile } = useWindowSize()
  const [emailVerificationModal, showEmailVerificationModal] = useModal(
    ({ close, isOpen }) => (isOpen ? <EmailVerificationModal closeModal={close} /> : null),
    emailModalConfig
  )
  const shareToken = router.query.token as string
  useEffect(() => {
    const handleToken = async () => {
      try {
        const resCheck = await postAuthTokenCheck({ token: shareToken })
        if (!resCheck.ok) throw Error(resCheck?.error?.toString?.())

        if (resCheck.json?.email) {
          fireEvent('sh_email_viewed', {
            sender: resCheck.json?.source_email as string,
          })

          if (resCheck.json?.exist === true) {
            const resToken = await postAuthTokenProcess({ token: shareToken })
            if (!resToken.ok) throw Error(resToken?.error?.toString?.())

            toastSuccess('memories_saved_login')
          } else if (resCheck.json?.exist === false) {
            // Create account
            const password = nanoid(16)
            const resToken = await postAuthTokenProcess({
              token: shareToken,
              password: await hashPassword(password),
              password_plain: password,
            })
            if (!resToken.ok) throw Error(resToken?.error?.toString?.())
            toastSuccess('memories_saved')

            await handleAuthResponse(await postLogin(resCheck.json?.email, password), false, {
              onLoginSuccess: async () => {
                mutateAnalyticsData({ type: 'animation_email' })
                events.user_registered.fire()
              },
              onVerificationRequired: showEmailVerificationModal,
            })
          }
        } else {
          throw new Error('Missing email in response')
        }
      } catch (err) {
        logError(err?.message || 'Failed to handle memory share token')
      }
    }

    if (shareToken) void handleToken()
  }, [shareToken])

  useEffect(() => {
    if (memory.data && !memory.isValidating && !memory.data?.ok) {
      toastError('animation_not_exist')
      void Router.push('/memories')
    }
  }, [memory.data, memory.isValidating])

  useEffect(() => {
    if (isLogged != null && memory.data?.json?.uuid) {
      mutateAnalyticsData({
        loginState: isLogged ? 'logged_in' : 'not_logged_in',
        invited: true,
        type: 'animation_invited',
        page: 'memories_landing',
      })
      fireEvent('sh_animation_viewed', {
        driver: animation?.driver || 'none',
      })
      setSessionValue(SESSION_REFERRAL_MEMORY_UUID, memory.data.json.uuid)
    }
  }, [animation?.driver, isLogged, memory.data?.json?.uuid])

  const memoryData = memory?.data?.json ?? props.memoryData

  const name = formatName(memoryData?.first_name, memoryData?.last_name, {
    oneLetterLastName: true,
  })
  const memoryTitle = name
    ? t('memory.shared.title', { name: name })
    : t('memory.shared.title_no_name')

  return (
    <LifeStoriesContextProvider>
      <Layout
        header={<TopNavigationLandingPage variant="full" />}
        footer={<Footer variant="full" />}
      >
        <PageTitle title={t('memory.shared.meta_title')} />
        <PageMeta
          description={memoryTitle}
          image={memoryData?.original_file?.url}
          url={router.asPath.split('?')[0]}
        />
        {memoryData?.animated_files?.length ? (
          <Head>
            <meta key="og:type" property="og:type" content="video.other" />
            <meta
              key="og:video"
              property="og:video"
              content={memoryData?.animated_files?.[0]?.file?.url}
            />
          </Head>
        ) : (
          <Head>
            <meta property="og:image" content={memoryData?.original_file?.url} />
          </Head>
        )}

        {!memoryData ? (
          <BlockSpinner css={{ margin: '0 auto' }} />
        ) : (
          <>
            {emailVerificationModal}
            <SingingPortraitsHero
              isPublic
              title={memoryTitle}
              desc={t('memory.shared.desc')}
              memories={selectedMemory ? [selectedMemory] : undefined}
              shareItems={
                <MemoryShareItems
                  caption={t('memory.shared.caption')}
                  memory={{ ...memory.data?.json, selectedAnimation: animation || null }}
                />
              }
              extraActions={<SharedSPActions memory={selectedMemory} />}
              hideDragDrop
              showLicenseBelowImage
            />

            <Spacer size={[2, 1]} />

            <Block>
              <MediaMentionsRow
                mediaMentions={props.mediaMentions}
                title={t('memory.shared.media_mention_title')}
                hideQuotes
                limit={isMobile ? 5 : 7}
              />
            </Block>

            <Spacer size={[3, 8]} />

            <StoryPortraitsLandingPageSection
              storyPortraitAnimations={props.storyPortraitAnimations}
            />

            <Spacer size={[3, 8]} />

            <HelpCarousel />

            <Spacer size={[3, 8]} />

            <SingingPortraitsWizardCards />

            <Spacer size={[3, 8]} />

            <FaqTwoColumns content={props.faq} />

            <Spacer size={[3, 8]} />

            <HomepageCallToAction mirrored images={singingPortraitsHomepageImages} />

            <Spacer size={[3, 8]} />
          </>
        )}
      </Layout>
    </LifeStoriesContextProvider>
  )
}

export const getServerSideProps: GetServerSideProps<PageProps> = async (ctx) => {
  const mediaMentions = await getContentfulMediaMentions('global')
  const { uuid, animationUuid } = ctx.query
  const memoryRes = await apiFetcher(
    encodeQuery(`/api/v1/memory/public/${uuid}`, { animationUuid })
  )
  const faq = await getContentfulFAQ('singing-portraits')

  const storyPortraitAnimations = await getStoryPortraitAnimations()

  return {
    props: {
      ...(await getIntlProps(ctx, ['memory', 'life_stories'])),
      mediaMentions,
      memoryData: memoryRes?.ok ? memoryRes.json : null,
      faq,
      storyPortraitAnimations,
    },
  }
}

export default Page
